import React, { useState, useEffect, useRef } from 'react';

const conversationFlow = [
  { type: 'user-typing', text: 'Hi, I need help with a scholarship application.' },
  { type: 'naoko-typing', text: "Hello! I'm Naoko. I can help you find and apply for scholarships. What's your education level?" },
  { type: 'user-typing', text: "I'm starting university next fall." },
  { type: 'naoko-typing', text: "Great! Based on your profile, you might be eligible for the National Merit Scholarship. Let's check if you qualify." },
  { type: 'user-typing', text: "That sounds perfect. What documents do I need?" },
  { type: 'naoko-typing', text: "You'll need your ID, academic transcripts, and a personal statement. Would you like to upload them now?" },
  { type: 'user-typing', text: "Yes, uploading my documents now." },
  { type: 'naoko-typing', text: "Thanks! I've analyzed your documents and completed 80% of the application form for you. Please review the highlighted sections before we submit." },
  { type: 'naoko-typing', text: "Here's a quick summary of what we've done:", isSystem: true },
  { type: 'system', text: "✓ Verified eligibility\n✓ Prepared application form\n✓ Reviewed documents\n✓ Identified 3 additional scholarships", isSystem: true }
];

const ChatPhoneDemoEnhanced = () => {
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPhoneDarkMode, setIsPhoneDarkMode] = useState(false);
  const chatContainerRef = useRef(null);

  // Improved typing animation with variable speed
  useEffect(() => {
    if (currentStep >= conversationFlow.length) return;

    const nextMessage = conversationFlow[currentStep];
    // Ensure we only process 'typing' types here
    if (!nextMessage.type || !nextMessage.type.includes('typing')) {
      // If it's not a typing message, add it directly (like system messages)
      if(nextMessage.type === 'system'){
         setMessages(prev => [...prev, {
           type: 'system',
           text: nextMessage.text
         }]);
         // Move to next step after a short delay for system messages
         const pauseDuration = nextMessage.text.length * 20 > 500 ? nextMessage.text.length * 20 : 500; // Adjust delay as needed
         setTimeout(() => setCurrentStep(prev => prev + 1), pauseDuration);
      } else {
          // Skip non-typing, non-system messages in this effect
          // Or handle other types if necessary
          setCurrentStep(prev => prev + 1); // Immediately move to next step
      }
      return;
    }


    setIsTyping(true);
    setCurrentInput('');

    const isUser = nextMessage.type.startsWith('user');
    const textToType = nextMessage.text;
    let charIndex = 0;

    // Variable typing speed for more natural feel
    const getTypingSpeed = () => {
      const baseSpeed = isUser ? 40 : 50;
      return baseSpeed + (Math.random() * 20 - 10);
    };

    const typeNextCharacter = () => {
      if (charIndex < textToType.length) {
        setCurrentInput(prev => prev + textToType.charAt(charIndex));
        charIndex++;
        setTimeout(typeNextCharacter, getTypingSpeed());
      } else {
        setIsTyping(false);
        setMessages(prev => [...prev, {
          type: isUser ? 'user' : nextMessage.isSystem ? 'system' : 'naoko',
          text: textToType
        }]);
        setCurrentInput('');

        const pauseDuration = Math.min(Math.max(textToType.length * 30, 800), 2000);
        setTimeout(() => setCurrentStep(prev => prev + 1), pauseDuration);
      }
    };

    // Start typing after a short initial delay
    const typingTimeout = setTimeout(typeNextCharacter, 200);
    return () => clearTimeout(typingTimeout);
  }, [currentStep]);

  // Enhanced auto-scroll with edge detection
  useEffect(() => {
    if (!chatContainerRef.current) return;

    const container = chatContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;

    if (isNearBottom || currentInput) {
       // Use 'auto' for instant scroll when not typing, 'smooth' during typing for better UX
      container.scrollTo({
        top: container.scrollHeight,
        behavior: isTyping ? 'smooth' : 'auto'
      });
    }
  }, [messages, currentInput, isTyping]); // Add isTyping dependency

  // Restart conversation with fade effect
  useEffect(() => {
    if (currentStep >= conversationFlow.length) {
      const timer = setTimeout(() => {
        // Add fade-out effect if desired (optional)
        setMessages([]);
        setCurrentStep(0);
      }, 5000); // Restart after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Toggle dark mode for the phone
  const toggleDarkMode = () => {
    setIsPhoneDarkMode(!isPhoneDarkMode);
  };

  // NOTE: Removed the outer .phone-demo-container div
  return (
    <div className={`phone-frame ${isPhoneDarkMode ? 'dark-mode' : ''}`}>
      <div className="phone-notch"></div>
      <div className="phone-screen">
        <div className="status-bar">
          <span>9:41</span>
          <div className="status-icons">
            {/* Simplified icons for clarity */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg> {/* Signal */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3C6.48 3 2 7.48 2 13h2c0-4.42 3.58-8 8-8s8 3.58 8 8h2c0-5.52-4.48-10-10-10zm0 4C8.69 7 6 9.69 6 13h2c0-2.76 2.24-5 5-5s5 2.24 5 5h2c0-3.31-2.69-6-6-6zm0 4c-1.66 0-3 1.34-3 3h6c0-1.66-1.34-3-3-3z"/></svg> {/* WiFi */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17 4h-3V2h-4v2H7c-1.1 0-2 .9-2 2v15c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 15H7V6h10v13z"/></svg> {/* Battery */}
          </div>
        </div>

        <div className="chat-app">
          <div className="chat-header">
            <div className="contact-info">
              <div className="avatar">N</div>
              <div>
                <h3>Naoko</h3>
                <p>Scholarship Assistant</p>
              </div>
            </div>
            <button aria-label="Toggle phone theme" className="dark-mode-toggle" onClick={toggleDarkMode}>
              {isPhoneDarkMode ? '☀️' : '🌙'}
            </button>
          </div>

          <div className="chat-messages" ref={chatContainerRef}>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`chat-bubble ${message.type}`}
                style={{
                  animation: `fadeIn 0.3s ${index * 0.05}s ease-out both` // Staggered animation
                }}
              >
                {message.type === 'system' ? (
                  <div className="system-message">
                    {/* Ensure text is treated as string before split */}
                    {(message.text || '').split('\n').map((line, i) => (
                      <p key={i} className="system-line">{line.startsWith('✓') ? '' : '✓ '}{line.replace('✓ ','')}</p>
                    ))}
                  </div>
                ) : (
                  <p>{message.text}</p>
                )}
              </div>
            ))}

            {isTyping && currentStep < conversationFlow.length && conversationFlow[currentStep].type.includes('typing') && (
              <div className={`chat-bubble ${conversationFlow[currentStep].type.startsWith('user') ? 'user' : 'naoko'} typing`}>
                <p>
                  {currentInput}
                  <span className="cursor" style={{ animationDelay: '0.5s' }}>|</span>
                  <span className="typing-dots">
                    <span className="dot" style={{ animationDelay: '0.1s' }}></span>
                    <span className="dot" style={{ animationDelay: '0.2s' }}></span>
                    <span className="dot" style={{ animationDelay: '0.3s' }}></span>
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="chat-input">
            <div className="input-field">
              <input
                type="text"
                placeholder="Type your message..."
                // Show typing user input in the field
                value={(isTyping && currentStep < conversationFlow.length && conversationFlow[currentStep].type === 'user-typing') ? currentInput : ''}
                readOnly
              />
              <button
                className={(isTyping && currentStep < conversationFlow.length && conversationFlow[currentStep].type === 'user-typing') ? 'active' : ''}
                aria-label="Send message"
                disabled
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>

            <div className="quick-actions">
              <button disabled>
                <span role="img" aria-label="attach" className="icon">📎</span> Upload Files
              </button>
              <button disabled>
                <span role="img" aria-label="refresh" className="icon">🔄</span> Check Status
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="phone-home-indicator"></div>

      {/* Styles remain the same, but without the .phone-demo-container rules */}
      <style jsx>{`
        /* REMOVED .phone-demo-container styles */

        .phone-frame {
          position: relative;
          width: 375px; /* Original width */
          height: 812px; /* Original height */
          border: 12px solid #111;
          border-radius: 50px;
          background: #111;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3),
                      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
          overflow: hidden;
          transition: all 0.3s ease;
          /* Will be scaled by parent */
        }

        .phone-frame.dark-mode {
          border-color: #333;
          background: #333;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5),
                      0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }

        .phone-notch {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 40%;
          height: 30px;
          background: #111; /* Match frame border */
          border-bottom-left-radius: 18px;
          border-bottom-right-radius: 18px;
          z-index: 10;
          transition: background 0.3s ease;
        }

        .phone-frame.dark-mode .phone-notch {
          background: #333; /* Match dark frame border */
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          background: white;
          overflow: hidden; /* Important for contained scrolling */
          display: flex;
          flex-direction: column;
          transition: background 0.3s ease;
          border-radius: 38px; /* Inner radius matching outer */
        }

        .phone-frame.dark-mode .phone-screen {
          background: #121212;
        }

        .phone-home-indicator {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          width: 120px;
          height: 5px;
          background: rgba(0, 0, 0, 0.3); /* Darker on light bg */
          border-radius: 3px;
          transition: background 0.3s ease;
        }

        .phone-frame.dark-mode .phone-home-indicator {
          background: rgba(255, 255, 255, 0.15); /* Lighter on dark bg */
        }

        /* Status Bar */
        .status-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 20px 8px; /* Adjusted padding */
            height: 44px; /* Typical iOS status bar height */
            box-sizing: border-box;
            color: #333;
            font-size: 13px; /* Smaller font size */
            font-weight: 600; /* Semi-bold */
            position: absolute; /* Position over chat content */
            top: 0;
            left: 0;
            right: 0;
            z-index: 5;
             /* Background slightly transparent or matching screen */
             background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(5px);
            transition: all 0.3s ease;
         }

        .phone-frame.dark-mode .status-bar {
          color: #eee;
          background: rgba(18, 18, 18, 0.8); /* Dark mode background */
        }

        .status-icons {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .status-icons svg {
           opacity: 0.8;
        }


        /* Chat app styles */
        .chat-app {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: #f8fafd; /* Default light background */
          transition: background 0.3s ease;
          padding-top: 44px; /* Push content below status bar */
          height: 100%; /* Ensure it takes full screen height */
          box-sizing: border-box;
        }

        .phone-frame.dark-mode .chat-app {
          background: #121212;
        }

        .chat-header {
          padding: 10px 16px;
          border-bottom: 1px solid #e1e5ee;
          background: white;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0; /* Prevent header from shrinking */
          transition: all 0.3s ease;
          position: relative; /* Needed for z-index if any absolute elements inside */
          z-index: 1;
        }

        .phone-frame.dark-mode .chat-header {
          background: #1e1e1e;
          border-bottom-color: #333;
        }

        .contact-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #1a237e;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 18px;
          transition: all 0.3s ease;
        }

        .phone-frame.dark-mode .avatar {
          background: #3949ab;
        }

        .contact-info h3 {
          margin: 0;
          font-size: 16px;
          color: #333;
          transition: color 0.3s ease;
        }

        .phone-frame.dark-mode .contact-info h3 {
          color: #eee;
        }

        .contact-info p {
          margin: 0;
          font-size: 12px;
          color: #666;
          transition: color 0.3s ease;
        }

        .phone-frame.dark-mode .contact-info p {
          color: #aaa;
        }

        .dark-mode-toggle {
          background: transparent;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .dark-mode-toggle:hover {
          background: rgba(0,0,0,0.05);
        }

        .phone-frame.dark-mode .dark-mode-toggle:hover {
          background: rgba(255,255,255,0.1);
        }

        /* Message bubbles */
        .chat-messages {
          flex: 1; /* Take remaining space */
          padding: 16px;
          overflow-y: auto; /* Enable scrolling */
          display: flex;
          flex-direction: column;
          gap: 12px;
          scroll-behavior: smooth;
        }

        .chat-bubble {
          padding: 10px 14px; /* Slightly smaller padding */
          border-radius: 18px; /* More rounded */
          max-width: 80%;
          font-size: 0.9rem; /* Slightly smaller font */
          line-height: 1.4;
          position: relative;
          word-wrap: break-word;
          transition: all 0.3s ease;
          /* animation defined inline now */
        }

        .chat-bubble p {
          margin: 0;
        }

        .chat-bubble.user {
          background: #e5e9f5;
          color: #1a1a1a;
          margin-left: auto;
          text-align: left;
          border-bottom-right-radius: 6px; /* Adjusted radius */
        }

        .phone-frame.dark-mode .chat-bubble.user {
          background: #2d3748; /* Darker blue-gray */
          color: #e2e8f0;
        }

        .chat-bubble.naoko {
          background: #1a237e;
          color: white;
          margin-right: auto;
          text-align: left;
          border-bottom-left-radius: 6px; /* Adjusted radius */
        }

        .phone-frame.dark-mode .chat-bubble.naoko {
          background: #3949ab;
        }

        .chat-bubble.system {
          background: #f0f4f8;
          color: #4a5568;
          margin: 8px auto; /* Center horizontally */
          text-align: left; /* Align text left for readability */
          border-radius: 12px;
          max-width: 90%;
          font-size: 0.8rem; /* Even smaller for system messages */
          padding: 8px 12px;
          border: 1px solid #e2e8f0; /* Subtle border */
        }

       .phone-frame.dark-mode .chat-bubble.system {
         background: #222b38; /* Darker system bg */
         color: #cbd5e0;
         border-color: #2d3748;
       }


        .system-message {
          display: flex;
          flex-direction: column;
          gap: 3px; /* Smaller gap */
        }

        .system-line {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 6px;
          color: #5a6a85; /* Slightly muted color */
        }

        .phone-frame.dark-mode .system-line {
            color: #a0aec0;
        }

        .chat-bubble.typing {
          opacity: 0.9;
        }

        /* Enhanced typing indicator */
        .typing-dots {
          display: inline-flex;
          align-items: center;
          height: 1em;
          margin-left: 4px;
          vertical-align: middle;
        }

        .dot {
          display: inline-block;
          width: 5px; /* Smaller dots */
          height: 5px;
          border-radius: 50%;
          background: currentColor;
          margin: 0 1.5px; /* Closer spacing */
          opacity: 0.4;
          animation: dotPulse 1.5s infinite ease-in-out;
        }

        .cursor {
          display: inline-block;
          width: 1px; /* Thinner cursor */
          height: 1em;
          background: currentColor;
          animation: blink 1s infinite step-end;
          vertical-align: text-bottom;
          margin-left: 2px;
        }

        /* Input area */
        .chat-input {
          padding: 10px 12px 12px; /* Adjusted padding */
          border-top: 1px solid #e1e5ee;
          background: white;
          flex-shrink: 0; /* Prevent shrinking */
          transition: all 0.3s ease;
          position: relative; /* Needed for z-index if any absolute elements inside */
          z-index: 1;
        }

        .phone-frame.dark-mode .chat-input {
          background: #1e1e1e;
          border-top-color: #333;
        }

        .input-field {
          display: flex;
          gap: 10px;
          background: #f3f5fa;
          padding: 6px 8px 6px 12px; /* Adjusted padding */
          border-radius: 20px; /* Slightly less round */
          align-items: center;
          transition: all 0.3s ease;
          border: 1px solid transparent; /* Placeholder for focus */
        }

        .input-field:focus-within {
           border-color: #aab5fa; /* Subtle focus */
           background: white;
        }


        .phone-frame.dark-mode .input-field {
          background: #2d3748;
        }

        .phone-frame.dark-mode .input-field:focus-within {
           border-color: #4a5568;
           background: #1e1e1e;
        }


        .input-field input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 0.9rem; /* Smaller input font */
          outline: none;
          padding: 4px 0; /* Adjust vertical padding */
          color: #333;
          transition: color 0.3s ease;
        }

        .phone-frame.dark-mode .input-field input {
          color: #e2e8f0;
        }

        .input-field input::placeholder {
          color: #999;
          transition: color 0.3s ease;
        }

        .phone-frame.dark-mode .input-field input::placeholder {
          color: #718096;
        }

        .input-field button {
          background: #1a237e;
          color: white;
          border: none;
          width: 30px; /* Smaller button */
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          opacity: 0.7;
          flex-shrink: 0;
        }

        .input-field button svg {
           width: 16px; /* Smaller icon */
           height: 16px;
        }

        .phone-frame.dark-mode .input-field button {
          background: #3949ab;
        }

        .input-field button.active {
          opacity: 1;
          transform: scale(1.05);
        }

        .input-field button:disabled {
          cursor: default;
          /* Keep some opacity even when disabled for visual */
        }

        /* Quick actions */
        .quick-actions {
          display: flex;
          gap: 8px; /* Smaller gap */
          margin-top: 10px; /* Smaller margin */
          padding: 0 6px;
          justify-content: center; /* Center the buttons */
        }

        .quick-actions button {
          background: transparent;
          border: 1px solid #d1d9e6;
          color: #1a237e;
          font-size: 0.75rem; /* Smaller font */
          font-weight: 500;
          padding: 4px 8px; /* Smaller padding */
          border-radius: 14px; /* Adjust radius */
          cursor: default;
          transition: all 0.2s;
          opacity: 0.6;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .phone-frame.dark-mode .quick-actions button {
          border-color: #4a5568;
          color: #cbd5e0;
        }

        .icon {
          font-size: 0.8rem; /* Slightly smaller icon */
          line-height: 1; /* Prevent extra space */
          display: inline-block; /* Better alignment */
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); } /* Less movement */
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        @keyframes dotPulse {
          0%, 100% {
            opacity: 0.4;
            transform: scale(0.9) translateY(0); /* Add scale */
          }
          50% {
            opacity: 1;
            transform: scale(1) translateY(-1.5px); /* Adjust movement */
          }
        }
      `}</style>
    </div>
  );
};

export default ChatPhoneDemoEnhanced;